import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Switch,
  FormControlLabel,
  Stack,
  Divider,
  Slider,
  Box as MuiBox,
  SelectChangeEvent
} from '@mui/material';
import {
  FilterList as FilterIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  Business as OrganizationIcon,
  Business,
  TrendingUp as UtilizationIcon,
  LocalHospital,
  PieChart,
  Route,
  Edit as EditIcon,
  Schedule,
  LocationOn,
  HealthAndSafety,
  MedicalServices,
  Payment,
  Visibility
} from '@mui/icons-material';
import FilterToggleButton from '../../components/map/FilterToggleButton';
import { filterPanelStyles } from '../../styles/map/filterPanelStyles';
import { MapContainer, TileLayer, ZoomControl, ScaleControl } from 'react-leaflet';

// Temporary placeholder for EncounterLayer until it's implemented
const EncounterLayer: React.FC<any> = () => null;

interface MarketIntelligenceProps {
  renderMap: () => React.ReactNode;
}

const MarketIntelligence: React.FC<MarketIntelligenceProps> = ({ renderMap: parentRenderMap }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [encounterTypes, setEncounterTypes] = useState<string[]>([]);
  const [selectedIcdCodes, setSelectedIcdCodes] = useState<string[]>([]);
  const [selectedCptCodes, setSelectedCptCodes] = useState<string[]>([]);
  const [selectedHcpcsCodes, setSelectedHcpcsCodes] = useState<string[]>([]);
  const [timePeriod, setTimePeriod] = useState('6months');
  const [customDateRange, setCustomDateRange] = useState({ start: new Date(), end: new Date() });
  const [geographyType, setGeographyType] = useState('zip');
  const [geographyValue, setGeographyValue] = useState('');
  const [showShortageAreas, setShowShortageAreas] = useState(false);
  const [selectedSpecialties, setSelectedSpecialties] = useState<string[]>([]);
  const [selectedOrganizations, setSelectedOrganizations] = useState<string[]>([]);
  const [selectedPayors, setSelectedPayors] = useState<string[]>([]);
  const [showHeatmap, setShowHeatmap] = useState(true);
  const [showPoints, setShowPoints] = useState(true);
  const [travelTime, setTravelTime] = useState<number>(15);
  const [encounterGroup, setEncounterGroup] = useState<string>('unique');
  const [encounters, setEncounters] = useState<Array<{
    id: string;
    latitude: number;
    longitude: number;
    type: string;
    date: string;
    icdCodes: string[];
    cptCodes: string[];
    hcpcsCodes: string[];
    specialty: string;
    organization: string;
    payor: string;
  }>>([]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleEncounterTypesChange = (event: SelectChangeEvent<string[]>) => {
    setEncounterTypes(event.target.value as string[]);
  };

  const handleIcdCodesChange = (event: SelectChangeEvent<string[]>) => {
    setSelectedIcdCodes(event.target.value as string[]);
  };

  const handleCptCodesChange = (event: SelectChangeEvent<string[]>) => {
    setSelectedCptCodes(event.target.value as string[]);
  };

  const handleHcpcsCodesChange = (event: SelectChangeEvent<string[]>) => {
    setSelectedHcpcsCodes(event.target.value as string[]);
  };

  const handleTimePeriodChange = (event: SelectChangeEvent<string>) => {
    setTimePeriod(event.target.value);
  };

  const handleCustomDateChange = (part: 'start' | 'end', dateStr: string) => {
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      setCustomDateRange(prev => ({ ...prev, [part]: date }));
    }
  };

  const handleGeographyTypeChange = (event: SelectChangeEvent<string>) => {
    setGeographyType(event.target.value);
  };

  const handleDrawBoundary = () => {
    // Implementation of drawing boundary
  };

  const handleTravelTimeChange = (event: SelectChangeEvent<number>) => {
    setTravelTime(event.target.value as number);
  };

  const handleShortageAreasChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowShortageAreas(event.target.checked);
  };

  const handleSpecialtiesChange = (event: SelectChangeEvent<string[]>) => {
    setSelectedSpecialties(event.target.value as string[]);
  };

  const handleOrganizationsChange = (event: SelectChangeEvent<string[]>) => {
    setSelectedOrganizations(event.target.value as string[]);
  };

  const handlePayorsChange = (event: SelectChangeEvent<string[]>) => {
    setSelectedPayors(event.target.value as string[]);
  };

  const handleHeatmapChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowHeatmap(event.target.checked);
  };

  const handlePointsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowPoints(event.target.checked);
  };

  const handleEncounterGroupChange = (event: SelectChangeEvent<string>) => {
    setEncounterGroup(event.target.value);
  };

  const renderCompetitorFilters = () => (
    <Stack spacing={2}>
      {/* Organization Filter */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <OrganizationIcon fontSize="small" color="primary" />
            Organization
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <TextField
              fullWidth
              size="small"
              label="Healthcare Organization Name"
              placeholder="Search organizations..."
            />
            <TextField
              fullWidth
              size="small"
              label="Parent Organization Name"
              placeholder="Search parent organizations..."
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Specialty & Service Lines Filter */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalHospital fontSize="small" color="primary" />
            Specialties & Services
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Specialty/Subspecialty</InputLabel>
              <Select multiple value={[]} onChange={() => {}}>
                <MenuItem value="cardiology">Cardiology</MenuItem>
                <MenuItem value="orthopedics">Orthopedics</MenuItem>
                <MenuItem value="neurology">Neurology</MenuItem>
                <MenuItem value="oncology">Oncology</MenuItem>
                <MenuItem value="pediatrics">Pediatrics</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Service Lines</InputLabel>
              <Select multiple value={[]} onChange={() => {}}>
                <MenuItem value="primary">Primary Care</MenuItem>
                <MenuItem value="emergency">Emergency Medicine</MenuItem>
                <MenuItem value="surgery">Surgery</MenuItem>
                <MenuItem value="diagnostic">Diagnostic Services</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Patient Volume Filter */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <UtilizationIcon fontSize="small" color="primary" />
            Patient Volume
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Time Period</InputLabel>
              <Select value="1year" onChange={() => {}}>
                <MenuItem value="1month">Last Month</MenuItem>
                <MenuItem value="6months">Last 6 Months</MenuItem>
                <MenuItem value="ytd">Year to Date</MenuItem>
                <MenuItem value="1year">Last Year</MenuItem>
                <MenuItem value="5years">Last 5 Years</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>
            <Box>
              <Typography variant="caption" color="text.secondary">Volume Range</Typography>
              <Slider
                value={[0, 1000]}
                onChange={() => {}}
                valueLabelDisplay="auto"
                min={0}
                max={10000}
              />
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Market Share Filter */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PieChart fontSize="small" color="primary" />
            Market Share
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Filter By</InputLabel>
              <Select value="service" onChange={() => {}}>
                <MenuItem value="service">Service Line</MenuItem>
                <MenuItem value="specialty">Specialty</MenuItem>
                <MenuItem value="subspecialty">Subspecialty</MenuItem>
              </Select>
            </FormControl>
            <Box>
              <Typography variant="caption" color="text.secondary">Market Share Range (%)</Typography>
              <Slider
                value={[0, 100]}
                onChange={() => {}}
                valueLabelDisplay="auto"
                min={0}
                max={100}
              />
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Shared Patient Journeys Filter */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Route fontSize="small" color="primary" />
            Shared Patient Journeys
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            {/* Referral Source */}
            <FormControl fullWidth size="small">
              <InputLabel>Referral Source</InputLabel>
              <Select multiple value={[]} onChange={() => {}}>
                <MenuItem value="inpatient">Inpatient</MenuItem>
                <MenuItem value="outpatient">Outpatient</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>

            {/* Specialty/Service Line */}
            <FormControl fullWidth size="small">
              <InputLabel>Specialty/Service Line</InputLabel>
              <Select multiple value={[]} onChange={() => {}}>
                <MenuItem value="cardiology">Cardiology</MenuItem>
                <MenuItem value="orthopedics">Orthopedics</MenuItem>
                <MenuItem value="primary">Primary Care</MenuItem>
              </Select>
            </FormControl>

            {/* Referring Provider */}
            <TextField
              fullWidth
              size="small"
              label="Referring Provider"
              placeholder="Search providers..."
            />

            {/* Geographic Boundaries */}
            <FormControl fullWidth size="small">
              <InputLabel>Geographic Boundary</InputLabel>
              <Select value="county" onChange={() => {}}>
                <MenuItem value="county">County</MenuItem>
                <MenuItem value="zip">ZIP Code</MenuItem>
                <MenuItem value="census">Census Tract</MenuItem>
                <MenuItem value="block">Block Group</MenuItem>
                <MenuItem value="trade">Trade Area</MenuItem>
                <MenuItem value="custom">Custom (Draw on Map)</MenuItem>
              </Select>
            </FormControl>

            {/* Time Range */}
            <FormControl fullWidth size="small">
              <InputLabel>Time Range</InputLabel>
              <Select value="1year" onChange={() => {}}>
                <MenuItem value="1month">Last Month</MenuItem>
                <MenuItem value="6months">Last 6 Months</MenuItem>
                <MenuItem value="ytd">Year to Date</MenuItem>
                <MenuItem value="1year">Last Year</MenuItem>
                <MenuItem value="5years">Last 5 Years</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>

            {/* Revenue Range */}
            <Box>
              <Typography variant="caption" color="text.secondary">Revenue Range ($)</Typography>
              <Slider
                value={[0, 1000000]}
                onChange={() => {}}
                valueLabelDisplay="auto"
                min={0}
                max={10000000}
                valueLabelFormat={(value) => `$${value.toLocaleString()}`}
              />
            </Box>

            {/* Journey Visualization Controls */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>Journey Visualization</Typography>
              <FormControlLabel
                control={<Switch size="small" />}
                label="Show Out-Migration Points"
              />
              <FormControlLabel
                control={<Switch size="small" />}
                label="Show Predictive Pathways"
              />
            </Box>

            {/* Prediction Options */}
            <FormControl fullWidth size="small">
              <InputLabel>Prediction Range</InputLabel>
              <Select value="6month" onChange={() => {}}>
                <MenuItem value="6month">6 Month Prediction</MenuItem>
                <MenuItem value="1year">1 Year Prediction</MenuItem>
                <MenuItem value="5year">5 Year Prediction</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );

  const renderUtilizationFilters = () => (
    <Stack spacing={2}>
      {/* Encounter Group */}
      <Accordion>
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{
            minHeight: 56,
            '& .MuiAccordionSummary-content': {
              m: 0
            }
          }}
        >
          <Typography 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              fontWeight: 500
            }}
          >
            <FilterIcon fontSize="small" color="primary" />
            Encounter Group
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2, pt: 0 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Encounter Type</InputLabel>
            <Select value={encounterGroup} onChange={handleEncounterGroupChange}>
              <MenuItem value="unique">Unique Encounters</MenuItem>
              <MenuItem value="total">Total Encounters</MenuItem>
            </Select>
          </FormControl>
        </AccordionDetails>
      </Accordion>

      {/* Encounters by Type */}
      <Accordion>
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{
            minHeight: 56,
            '& .MuiAccordionSummary-content': {
              m: 0
            }
          }}
        >
          <Typography 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              fontWeight: 500
            }}
          >
            <LocalHospital fontSize="small" color="primary" />
            Encounters by Type
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2, pt: 0 }}>
          <Stack spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Encounter Type</InputLabel>
              <Select multiple value={encounterTypes} onChange={handleEncounterTypesChange}>
                <MenuItem value="inpatient">Inpatient</MenuItem>
                <MenuItem value="outpatient">Outpatient</MenuItem>
              </Select>
            </FormControl>
            
            {/* ICD Codes */}
            <FormControl fullWidth size="small">
              <InputLabel>ICD-10 Codes</InputLabel>
              <Select multiple value={selectedIcdCodes} onChange={handleIcdCodesChange}>
                {/* Populate with actual ICD codes from data */}
              </Select>
            </FormControl>

            {/* CPT Codes */}
            <FormControl fullWidth size="small">
              <InputLabel>CPT Codes</InputLabel>
              <Select multiple value={selectedCptCodes} onChange={handleCptCodesChange}>
                {/* Populate with actual CPT codes from data */}
              </Select>
            </FormControl>

            {/* HCPCS Codes */}
            <FormControl fullWidth size="small">
              <InputLabel>HCPCS Codes</InputLabel>
              <Select multiple value={selectedHcpcsCodes} onChange={handleHcpcsCodesChange}>
                {/* Populate with actual HCPCS codes from data */}
              </Select>
            </FormControl>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Encounters by Timeframe */}
      <Accordion>
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{
            minHeight: 56,
            '& .MuiAccordionSummary-content': {
              m: 0
            }
          }}
        >
          <Typography 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              fontWeight: 500
            }}
          >
            <Schedule fontSize="small" color="primary" />
            Encounters by Timeframe
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2, pt: 0 }}>
          <Stack spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Time Period</InputLabel>
              <Select value={timePeriod} onChange={handleTimePeriodChange}>
                <MenuItem value="6months">Last 6 Months</MenuItem>
                <MenuItem value="ytd">Year to Date</MenuItem>
                <MenuItem value="12months">Last 12 Months</MenuItem>
                <MenuItem value="custom">Custom Date Range</MenuItem>
              </Select>
            </FormControl>
            
            {timePeriod === 'custom' && (
              <Stack spacing={2}>
                <TextField
                  type="date"
                  label="Start Date"
                  value={customDateRange.start.toISOString().split('T')[0]}
                  onChange={(e) => handleCustomDateChange('start', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
                <TextField
                  type="date"
                  label="End Date"
                  value={customDateRange.end.toISOString().split('T')[0]}
                  onChange={(e) => handleCustomDateChange('end', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Stack>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Encounters by Geography */}
      <Accordion>
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{
            minHeight: 56,
            '& .MuiAccordionSummary-content': {
              m: 0
            }
          }}
        >
          <Typography 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              fontWeight: 500
            }}
          >
            <LocationOn fontSize="small" color="primary" />
            Encounters by Geography
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2, pt: 0 }}>
          <Stack spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Geographic Boundary</InputLabel>
              <Select value={geographyType} onChange={handleGeographyTypeChange}>
                <MenuItem value="zip">ZIP Code</MenuItem>
                <MenuItem value="county">County</MenuItem>
                <MenuItem value="city">City</MenuItem>
                <MenuItem value="state">State</MenuItem>
                <MenuItem value="census">Census Tract</MenuItem>
                <MenuItem value="block">Block Group</MenuItem>
                <MenuItem value="custom">Custom Boundary</MenuItem>
                <MenuItem value="trade">Trade Area</MenuItem>
              </Select>
            </FormControl>

            {geographyType === 'custom' && (
              <Button
                variant="outlined"
                size="small"
                onClick={handleDrawBoundary}
                startIcon={<EditIcon />}
              >
                Draw Boundary
              </Button>
            )}

            {geographyType === 'trade' && (
              <Stack spacing={2}>
                <TextField
                  size="small"
                  label="Point of Interest"
                  placeholder="Search location..."
                />
                <FormControl fullWidth size="small">
                  <InputLabel>Travel Time</InputLabel>
                  <Select value={travelTime} onChange={handleTravelTimeChange}>
                    <MenuItem value={15}>15 minutes</MenuItem>
                    <MenuItem value={30}>30 minutes</MenuItem>
                    <MenuItem value={45}>45 minutes</MenuItem>
                    <MenuItem value={60}>60 minutes</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Healthcare Shortage Areas */}
      <Accordion>
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{
            minHeight: 56,
            '& .MuiAccordionSummary-content': {
              m: 0
            }
          }}
        >
          <Typography 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              fontWeight: 500
            }}
          >
            <HealthAndSafety fontSize="small" color="primary" />
            Healthcare Shortage Areas
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2, pt: 0 }}>
          <FormControlLabel
            control={<Switch checked={showShortageAreas} onChange={handleShortageAreasChange} />}
            label={<Typography variant="body2">Show Healthcare Shortage Areas</Typography>}
          />
        </AccordionDetails>
      </Accordion>

      {/* Encounters by Specialty */}
      <Accordion>
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{
            minHeight: 56,
            '& .MuiAccordionSummary-content': {
              m: 0
            }
          }}
        >
          <Typography 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              fontWeight: 500
            }}
          >
            <MedicalServices fontSize="small" color="primary" />
            Encounters by Specialty
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2, pt: 0 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Specialties</InputLabel>
            <Select multiple value={selectedSpecialties} onChange={handleSpecialtiesChange}>
              {/* Populate with actual specialties from data */}
            </Select>
          </FormControl>
        </AccordionDetails>
      </Accordion>

      {/* Encounters by Healthcare Organization */}
      <Accordion>
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{
            minHeight: 56,
            '& .MuiAccordionSummary-content': {
              m: 0
            }
          }}
        >
          <Typography 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              fontWeight: 500
            }}
          >
            <Business fontSize="small" color="primary" />
            Encounters by Organization
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2, pt: 0 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Organizations</InputLabel>
            <Select multiple value={selectedOrganizations} onChange={handleOrganizationsChange}>
              {/* Populate with actual organizations from data */}
            </Select>
          </FormControl>
        </AccordionDetails>
      </Accordion>

      {/* Encounters by Payor */}
      <Accordion>
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{
            minHeight: 56,
            '& .MuiAccordionSummary-content': {
              m: 0
            }
          }}
        >
          <Typography 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              fontWeight: 500
            }}
          >
            <Payment fontSize="small" color="primary" />
            Encounters by Payor
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2, pt: 0 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Payors</InputLabel>
            <Select multiple value={selectedPayors} onChange={handlePayorsChange}>
              {/* Populate with actual payors from data */}
            </Select>
          </FormControl>
        </AccordionDetails>
      </Accordion>

      {/* Visualization Options */}
      <Accordion>
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{
            minHeight: 56,
            '& .MuiAccordionSummary-content': {
              m: 0
            }
          }}
        >
          <Typography 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              fontWeight: 500
            }}
          >
            <Visibility fontSize="small" color="primary" />
            Visualization Options
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2, pt: 0 }}>
          <Stack spacing={2}>
            <FormControlLabel
              control={<Switch checked={showHeatmap} onChange={handleHeatmapChange} />}
              label={<Typography variant="body2">Show Heatmap</Typography>}
            />
            <FormControlLabel
              control={<Switch checked={showPoints} onChange={handlePointsChange} />}
              label={<Typography variant="body2">Show Individual Points</Typography>}
            />
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );

  const renderMapContent = () => (
    <Box sx={{ height: '100%', width: '100%', position: 'relative' }}>
      <MapContainer
        center={[28.5383, -81.3792]}
        zoom={10}
        scrollWheelZoom={true}
        zoomControl={false}
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <ZoomControl position="topright" />
        <ScaleControl position="bottomright" />
        
        <EncounterLayer
          encounters={encounters}
          filters={{
            encounterTypes,
            icdCodes: selectedIcdCodes,
            cptCodes: selectedCptCodes,
            hcpcsCodes: selectedHcpcsCodes,
            timeframe: {
              start: customDateRange.start,
              end: customDateRange.end,
            },
            geography: {
              type: geographyType as 'zip' | 'county' | 'city' | 'state' | 'census' | 'block' | 'custom',
              value: geographyValue,
            },
            specialty: selectedSpecialties,
            organization: selectedOrganizations,
            payor: selectedPayors,
          }}
          showHeatmap={showHeatmap}
          showPoints={showPoints}
        />
      </MapContainer>
    </Box>
  );

  return (
    <Box sx={{ height: 'calc(100vh - 240px)', display: 'flex', flexDirection: 'column' }}>
      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          aria-label="market intelligence tabs"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              minWidth: 'auto',
              fontSize: '0.875rem'
            }
          }}
        >
          <Tab 
            icon={<OrganizationIcon sx={{ mr: 1 }} />} 
            iconPosition="start"
            label="Competitors & Third-Party Healthcare Organizations" 
          />
          <Tab 
            icon={<UtilizationIcon sx={{ mr: 1 }} />} 
            iconPosition="start"
            label="Utilization and Demand" 
          />
        </Tabs>
      </Box>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            {activeTab === 0 
              ? 'Analyze competitive landscape and market positioning'
              : 'Understand healthcare demand patterns and utilization trends'
            }
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <FilterToggleButton showFilters={showFilters} onToggle={() => setShowFilters(!showFilters)} />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="small"
          >
            New Analysis
          </Button>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ 
        flexGrow: 1,
        minHeight: 0,
        display: 'flex',
        gap: 0
      }}>
        {/* Filters Panel */}
        <Paper sx={filterPanelStyles.panel({ showFilters })}>
          {showFilters && (
            <Box sx={filterPanelStyles.container}>
              <Box sx={filterPanelStyles.content}>
                {activeTab === 0 ? renderCompetitorFilters() : renderUtilizationFilters()}
              </Box>
            </Box>
          )}
        </Paper>

        {/* Map Container */}
        <Paper 
          elevation={3} 
          sx={{ 
            flexGrow: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: showFilters ? '0 4px 4px 0' : '4px'
          }}
        >
          {renderMapContent()}
        </Paper>
      </Box>
    </Box>
  );
};

export default MarketIntelligence; 